import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

/* ============================================================
 * KEYFRAMES
 * ============================================================ */
const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/* ============================================================
 * MIXINS
 * ============================================================ */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * CONTAINER
 * ============================================================ */
export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em;
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  /* ============================================================
   * HEADER
   * ============================================================ */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 14px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "PROJETOS · GESTÃO DIGITAL";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};
          background: transparent !important;
          padding: 0 !important;
          border: none !important;
          opacity: 1 !important;

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          ${mono};
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          font-size: 0.9em;
          background: transparent !important;
          padding: 0 !important;
          border: none !important;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ============================================================
   * KPI CARDS (4 cols)
   * ============================================================ */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .thumb {
      opacity: 0.35;
      font-size: 0.9em;
      color: ${({ theme }) => theme.text};
      display: inline-flex;
      align-items: center;
    }

    .text {
      display: flex;
      flex-direction: column;
      gap: 2px;

      p:first-child {
        ${mono};
        margin: 0;
        font-size: 1.6em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.02em;
        line-height: 1.1;
      }

      p:last-child {
        margin: 2px 0 0 0;
        font-size: 0.72em;
        font-weight: 500;
        opacity: 0.5;
        color: ${({ theme }) => theme.text};
      }
    }

    /* Variantes: cor APENAS no thumb (ícone). Número fica branco/text */
    &.primary .thumb { color: ${({ theme }) => theme.text}; opacity: 0.55; }
    &.success .thumb { color: ${tokens.brand}; opacity: 0.85; }
    &.warning .thumb { color: ${tokens.warn}; opacity: 0.85; }
    &.info .thumb { color: #3b82f6; opacity: 0.85; }
  }

  /* ============================================================
   * TOOLBAR
   * ============================================================ */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 4px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 0;
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .btn-new-project {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: ${tokens.brand};
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      color: white;
      font-family: ${tokens.fontSans};
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      letter-spacing: -0.005em;
      white-space: nowrap;

      svg { font-size: 0.95em; }

      &:hover:not(:disabled) {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }
    }

    .search-input {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      max-width: 460px;
      min-width: 200px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      svg {
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        flex-shrink: 0;
      }

      input {
        flex: 1;
        background: transparent;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        letter-spacing: -0.005em;

        &::placeholder { opacity: 0.4; }
      }
    }
  }

  /* ============================================================
   * SCROLLABLE CONTENT
   * ============================================================ */
  .scrollable-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    margin-top: 6px;
    padding: 4px 6px 24px 0;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 3px;
    }
  }

  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;

    p {
      ${mono};
      font-size: 0.78em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
    }
  }

  /* ============================================================
   * PROJECTS GRID
   * ============================================================ */
  .projects-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;

    @media (max-width: 1400px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 720px)  { grid-template-columns: 1fr; }
  }

  /* ============================================================
   * PROJECT CARD
   * ============================================================ */
  .project-card {
    position: relative;
    ${({ theme }) => cardBase(theme)};
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    cursor: default;
    min-height: 180px;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
      .btn-edit-icon { opacity: 1; }
    }

    .btn-edit-icon {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 26px;
      height: 26px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusSm};
      color: ${({ theme }) => theme.text};
      cursor: pointer;
      opacity: 0;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      z-index: 2;

      svg { font-size: 0.85em; }

      &:hover {
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
        opacity: 1;
      }
    }

    /* Header: avatar + nome/cnpj */
    .project-card-header {
      display: grid;
      grid-template-columns: 32px 1fr;
      gap: 10px;
      align-items: center;
      padding-right: 36px;

      .project-avatar {
        display: inline-flex;

        .avatar-circle {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: ${tokens.radiusSm};
          color: white;
          flex-shrink: 0;

          svg { font-size: 1em; }
        }
      }

      .project-info {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;

        h5 {
          margin: 0;
          font-size: 0.88em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          text-transform: capitalize;
        }

        .project-cnpj {
          ${mono};
          margin: 0;
          font-size: 0.62em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          letter-spacing: 0.01em;
        }

        .project-id {
          ${mono};
          margin: 0;
          font-size: 0.58em;
          color: ${({ theme }) => theme.text};
          opacity: 0.35;
          letter-spacing: 0.01em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          cursor: pointer;
          transition: opacity 0.15s ease;

          &:hover {
            opacity: 0.7;
          }
        }
      }
    }

    /* Body: meta grid 2x2 */
    .project-card-body {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .project-meta {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    .meta-item {
      display: grid;
      grid-template-columns: 28px 1fr;
      gap: 10px;
      align-items: center;
      padding: 9px 11px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusSm};

      svg {
        font-size: 0.95em;
        color: ${({ theme }) => theme.text};
        opacity: 0.7;
      }

      .meta-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .meta-label {
        ${eyebrow};
        font-size: 0.58em;
        opacity: 0.55;
        color: ${({ theme }) => theme.text};
      }

      .meta-value {
        ${mono};
        font-size: 0.95em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 1;
        letter-spacing: -0.01em;
      }
    }
  }

  /* ============================================================
   * EMPTY STATE
   * ============================================================ */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 110px 20px 80px;
    background: transparent;
    border: none;
    text-align: center;

    > svg {
      width: 64px;
      height: 64px;
      padding: 18px;
      box-sizing: content-box;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: 50%;
      color: ${tokens.brand};
      opacity: 0.7;
      font-size: 1em;
    }

    h4 {
      margin: 0;
      font-size: 1em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
      max-width: 280px;
      line-height: 1.55;
    }
  }
`;
